import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, CheckCircle2, XCircle, Send, Eye, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/integrations/supabase/types';
import {
  ADMIN_OPEN_ISSUES_QUERY_KEY,
  type AdminOpenIssueRow,
} from '@/lib/adminOpenIssues';
import { releaseCleaningPropertyIssue, cleaningIssueErrorMessagePl } from '@/lib/cleaningIssueApi';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale/pl';
import { useAdminOpenIssues } from '@/hooks/useAdminOpenIssues';
import { StoragePhoto } from '@/components/StoragePhoto';

export type { AdminOpenIssueRow as PropertyIssue } from '@/lib/adminOpenIssues';

interface AdminDashboardIssuesProps {
  orgId: string;
}

export function AdminDashboardIssues({ orgId }: AdminDashboardIssuesProps) {
  const queryClient = useQueryClient();
  const {
    data,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useAdminOpenIssues(orgId);

  const issues: AdminOpenIssueRow[] = data?.pages.flatMap((p) => p) ?? [];
  const [selectedIssue, setSelectedIssue] = useState<AdminOpenIssueRow | null>(null);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);

  const showInitialSkeleton = isPending && !data;

  const handleUpdateIssue = async (
    issueId: string,
    updates: {
      status?: Database['public']['Enums']['issue_status_enum'];
      notification_status?: string;
    }
  ) => {
    try {
      setIsUpdating(issueId);

      const { error } = await supabase
        .from('property_issues')
        .update(updates)
        .eq('id', issueId);

      if (error) {
        console.error('Error updating issue:', error);
        toast.error('Błąd aktualizacji usterki');
        return;
      }

      toast.success('Usterka zaktualizowana');
      await queryClient.invalidateQueries({ queryKey: [ADMIN_OPEN_ISSUES_QUERY_KEY] });
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error('Wystąpił nieoczekiwany błąd');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleSendToClient = async (issueId: string) => {
    try {
      setIsUpdating(issueId);
      await releaseCleaningPropertyIssue(issueId);
      toast.success('Usterka przekazana');
      await queryClient.invalidateQueries({ queryKey: [ADMIN_OPEN_ISSUES_QUERY_KEY] });
    } catch (error) {
      console.error('Error releasing cleaning issue:', error);
      toast.error(cleaningIssueErrorMessagePl(error));
    } finally {
      setIsUpdating(null);
    }
  };

  const handleResolve = (issueId: string) => {
    handleUpdateIssue(issueId, { status: 'resolved' });
  };

  const handleIgnore = (issueId: string) => {
    handleUpdateIssue(issueId, { status: 'rejected' });
  };

  const getNotificationStatusBadge = (_issue: AdminOpenIssueRow) => {
    return (
      <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800">
        Oczekuje na przekazanie
      </Badge>
    );
  };

  if (showInitialSkeleton) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Zarządzanie usterkami</CardTitle>
          <CardDescription>Usterki oczekujące na przekazanie</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (issues.length === 0) {
    return (
      <Card className={isFetching ? 'opacity-90 transition-opacity' : undefined}>
        <CardHeader>
          <CardTitle>Zarządzanie usterkami</CardTitle>
          <CardDescription>Usterki oczekujące na przekazanie</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Brak usterek oczekujących na przekazanie</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={isFetching && !isFetchingNextPage ? 'opacity-[0.98] transition-opacity' : undefined}>
        <CardHeader>
          <CardTitle>Zarządzanie usterkami</CardTitle>
          <CardDescription>Usterki oczekujące na przekazanie{issues.length > 0 ? ` (${issues.length})` : ''}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {issues.map((issue) => (
              <div
                key={issue.id}
                className="border rounded-lg p-4 space-y-3 hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span className="font-semibold">
                        {issue.cleaning_locations?.address || 'Nieznany adres'}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {issue.description || 'Brak opisu'}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      {issue.reporter?.full_name && (
                        <span>Zgłaszający: {issue.reporter.full_name}</span>
                      )}
                      {issue.created_at && (
                        <span>
                          {format(new Date(issue.created_at), 'dd.MM.yyyy HH:mm', { locale: pl })}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {getNotificationStatusBadge(issue)}
                    {issue.photo_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedIssue(issue);
                          setIsImageDialogOpen(true);
                        }}
                        className="gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        Zobacz zdjęcie
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSendToClient(issue.id)}
                    disabled={isUpdating === issue.id}
                    className="flex-1 gap-2"
                  >
                    <Send className="h-4 w-4" />
                    Przekaż do klienta
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResolve(issue.id)}
                    disabled={isUpdating === issue.id}
                    className="flex-1 gap-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                  >
                    <CheckCircle2 className="h-4 w-4" />
                    Rozwiązane
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleIgnore(issue.id)}
                    disabled={isUpdating === issue.id}
                    className="flex-1 gap-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                  >
                    <XCircle className="h-4 w-4" />
                    Zignoruj
                  </Button>
                </div>
              </div>
            ))}
            {hasNextPage && (
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Ładowanie...
                    </>
                  ) : (
                    'Pokaż więcej'
                  )}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={isImageDialogOpen} onOpenChange={setIsImageDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Zdjęcie usterki</DialogTitle>
            <DialogDescription>
              {selectedIssue?.cleaning_locations?.address || 'Nieznany adres'}
            </DialogDescription>
          </DialogHeader>
          {selectedIssue?.photo_url && (
            <div className="relative w-full">
              <StoragePhoto
                pathOrUrl={selectedIssue.photo_url}
                fallbackBucket="property-issues"
                alt="Zdjęcie usterki"
                className="w-full h-auto rounded-lg"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
